import { useState, useCallback, useRef } from 'react';
import { useApi } from '@backstage/core-plugin-api';
import { grafanaApiRef } from '../api/grafanaApi';
import { QueryConfig, QueryRequest, QueryResponse, DataFrame } from '../types/grafana';

const DEFAULT_LIMIT = 100;

export const useGrafanaQuery = (queryConfig: QueryConfig | null) => {
  const [data, setData] = useState<QueryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [totalCount, setTotalCount] = useState<number | undefined>(undefined);
  const grafanaApi = useApi(grafanaApiRef);
  
  const isQueryingRef = useRef(false);
  const loadedDataRef = useRef<QueryResponse | null>(null);
  const loadedCountRef = useRef<number>(0);
  const oldestTimestampRef = useRef<number | null>(null);
  const originalRangeRef = useRef<{ from: string; to: string } | null>(null);

  const validateQueryConfig = (config: QueryConfig | null): boolean => {
    if (!config) return false;
    if (!config.datasourceUid) return false;
    if (!config.queries || config.queries.length === 0) return false;
    
    return config.queries.some(q => 
      (q.expr && q.expr.trim() !== '') || 
      (q.query && q.query.trim() !== '')
    );
  };

  const getFrameOldestTimestamp = (frame: DataFrame): number | null => {
    if (!frame.data?.values) return null;
    
    // Procurar pelo índice do campo de timestamp
    const timestampIndex = frame.schema?.fields?.findIndex(f => 
      f.name === 'Time' || f.name === 'timestamp' || f.name === 'ts'
    );
    
    if (timestampIndex === undefined || timestampIndex < 0) return null;
    
    const timestamps = frame.data.values[timestampIndex];
    if (!timestamps || !timestamps.length) return null;
    
    // Encontrar o timestamp mais antigo
    let oldestTimestamp: number | null = null;
    for (let i = 0; i < timestamps.length; i++) {
      const ts = new Date(timestamps[i]).getTime();
      if (oldestTimestamp === null || ts < oldestTimestamp) {
        oldestTimestamp = ts;
      }
    }
    
    return oldestTimestamp;
  };

  const extractOldestTimestamp = (result: QueryResponse): number | null => {
    if (!result?.results) return null;

    let oldestTimestamp: number | null = null;

    Object.values(result.results).forEach(resultObj => {
      if (!resultObj.frames) return;

      resultObj.frames.forEach(frame => {
        const frameOldestTimestamp = getFrameOldestTimestamp(frame);
        if (frameOldestTimestamp !== null && (oldestTimestamp === null || frameOldestTimestamp < oldestTimestamp)) {
          oldestTimestamp = frameOldestTimestamp;
        }
      });
    });

    return oldestTimestamp;
  };

  const countLogsInResult = (result: QueryResponse): number => {
    if (!result?.results) return 0;
    
    let totalLogs = 0;
    
    Object.values(result.results).forEach(resultObj => {
      if (!resultObj.frames) return;
      
      resultObj.frames.forEach(frame => {
        if (!frame.data?.values || !frame.data.values[0]) return;
        
        totalLogs += frame.data.values[0].length;
      });
    });
    
    return totalLogs;
  };

  const checkForMoreResults = (result: QueryResponse, limit: number): boolean => {
    if (!result?.results) return false;
    
    // Verificar se algum dos resultados tem exatamente o número limite de registros
    return Object.values(result.results).some(resultObj => {
      if (!resultObj.frames) return false;
      
      return resultObj.frames.some(frame => {
        if (!frame.data?.values || !frame.data.values[0]) return false;
        
        // Se o número de valores é igual ao limite, provavelmente há mais dados
        return frame.data.values[0].length >= limit;
      });
    });
  };

  const estimateTotalCount = (
    result: QueryResponse,
    offset: number,
    limit: number,
    moreAvailable: boolean,
    loadedCount: number
  ) => {
    if (!result?.results) return;

    let maxCount = 0;

    // Contar o número de registros em todos os frames
    Object.values(result.results).forEach(resultObj => {
      if (!resultObj.frames) return;

      resultObj.frames.forEach(frame => {
        if (!frame.data?.values || !frame.data.values[0]) return;

        const count = frame.data.values[0].length;
        if (count > maxCount) {
          maxCount = count;
        }
      });
    });

    // Calcular o total estimado
    // Se estamos carregando mais dados, usar o loadedCount como base
    const baseCount = offset > 0 ? loadedCount : maxCount;
    const newTotal = moreAvailable ? baseCount + limit : baseCount;

    setTotalCount(newTotal);
  };

  const executeQuery = useCallback(async (offset = 0, limit?: number, append = false) => {
    if (!validateQueryConfig(queryConfig) || isQueryingRef.current) {
      return;
    }

    try {
      isQueryingRef.current = true;
      setLoading(true);
      setError(null);

      const validQueries = queryConfig!.queries.filter(
        q => (q.expr && q.expr.trim() !== '') || (q.query && q.query.trim() !== '')
      );

      if (validQueries.length === 0) {
        throw new Error('Nenhuma query válida para executar');
      }

      const queryLimit = validQueries[0].limit || limit || DEFAULT_LIMIT;

      let range = queryConfig!.range;
      
      if (append && oldestTimestampRef.current && originalRangeRef.current) {
        const oldestDate = new Date(oldestTimestampRef.current);
        
        oldestDate.setMilliseconds(oldestDate.getMilliseconds() - 1);
        
        range = {
          from: originalRangeRef.current.from,
          to: oldestDate.toISOString(),
        };
        
      } else if (!append && queryConfig!.range && queryConfig!.range.from && queryConfig!.range.to) {
        originalRangeRef.current = { 
          from: queryConfig!.range.from, 
          to: queryConfig!.range.to 
        };
      }

      const paginatedQueries = validQueries.map(q => ({
        ...q,
        limit: q.limit || queryLimit,
        maxLines: q.limit || queryLimit,
      }));

      const datasourceUid = queryConfig!.datasourceUid || '';

      const request: QueryRequest = {
        datasourceUid,
        queries: paginatedQueries,
        range: range,
        interval: queryConfig!.interval,
        maxDataPoints: queryConfig!.maxDataPoints,
      };

      const result = await grafanaApi.query(request);
      
      const hasMoreResults = checkForMoreResults(result, queryLimit);
      setHasMore(hasMoreResults);
      
      const logsInThisResult = countLogsInResult(result);
            
      if (logsInThisResult === 0 && append) {
        setHasMore(false);
      }
      
      const oldestTimestamp = extractOldestTimestamp(result);
      if (oldestTimestamp) {
        oldestTimestampRef.current = oldestTimestamp;
      }
      
      if (append && loadedDataRef.current) {
        loadedCountRef.current += logsInThisResult;
        
        estimateTotalCount(result, offset, queryLimit, hasMoreResults, loadedCountRef.current);

        setData(result);
      } else {
        loadedCountRef.current = logsInThisResult;
        estimateTotalCount(result, 0, queryLimit, hasMoreResults, logsInThisResult);
        
        setData(result);
        loadedDataRef.current = result;
      }
      
      setLastRefreshed(new Date());
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error executing query:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
      isQueryingRef.current = false;
    }
    // Helpers `extractOldestTimestamp`, `countLogsInResult`, etc. são funções
    // locais ao hook (recriadas a cada render, mas estáveis em comportamento).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryConfig, grafanaApi]);

  const manualExecuteQuery = useCallback(() => {
    if (validateQueryConfig(queryConfig)) {
      loadedDataRef.current = null;
      loadedCountRef.current = 0;
      oldestTimestampRef.current = null;
      originalRangeRef.current = null;
      
      const queryLimit = queryConfig?.queries[0]?.limit;
      executeQuery(0, queryLimit, false);
    }
  }, [queryConfig, executeQuery]);

  const loadMoreData = useCallback((offset: number, limit?: number) => {
    if (validateQueryConfig(queryConfig)) {
      const queryLimit = queryConfig?.queries[0]?.limit || limit || DEFAULT_LIMIT;
      executeQuery(offset, queryLimit, true);
    }
  }, [queryConfig, executeQuery]);

  return { 
    data, 
    loading, 
    error, 
    lastRefreshed,
    hasMore,
    totalCount,
    refresh: manualExecuteQuery,
    execute: manualExecuteQuery,
    loadMore: loadMoreData
  };
};